// Keycloak auth via Sleek's own screens (direct-grant). authN only.
// Multi-realm: one login screen serves BOTH tenants (sleek-staging / sleek-web) and platform
// admins (sleek-admin / sleek-admin-web). Login tries each realm in order; the realm+client that
// succeeds is stored so token refresh uses the same. The stored realm also tells the app whether
// the signed-in user is a tenant or a platform admin.
// "Remember me" → localStorage (survives tab close); otherwise sessionStorage (this tab only).
// NOTE: browser storage is XSS-readable; the production hardening is an httpOnly cookie (TODO).
import { signal } from './t4.js';

const KC = 'https://auth.aboo.co.za';
// order matters: tenants are the common case, admins fall through
// The tenant realm is per-ENVIRONMENT, derived from the host so one build is correct everywhere:
// staging hosts (…-staging.aboo.co.za) and local/LAN dev → sleek-staging; production hosts
// (app.aboo.co.za, admin.aboo.co.za) → sleek-production. Previously hardcoded 'sleek-staging',
// which made production hosts authenticate tenants against the staging realm (prod tenants like a
// sleek-production accountant could not sign in). The admin realm is the same in both envs.
const TENANT_REALM = (() => {
  try {
    const h = (location.hostname || '').toLowerCase();
    if(h === 'localhost' || h.startsWith('127.') || /^\d+\.\d+\.\d+\.\d+$/.test(h)
       || /(^|[.-])staging([.-]|$)/.test(h)) return 'sleek-staging';
  } catch { /* no window (SSR/tests) — fall through to production */ }
  return 'sleek-production';
})();
const REALMS = [
  { realm: TENANT_REALM,   client: 'sleek-web',       kind: 'tenant' },
  { realm: 'sleek-admin',  client: 'sleek-admin-web', kind: 'admin'  },
];
const tokenUrl = realm => `${KC}/realms/${realm}/protocol/openid-connect/token`;

const K_TOKEN = 'sleek_admin_token';
const K_REFRESH = 'sleek_admin_refresh';
const K_USER = 'sleek_admin_user';
const K_REMEMBER = 'sleek_admin_remember';
const K_REALM = 'sleek_auth_realm';
const K_CLIENT = 'sleek_auth_client';
const K_KIND = 'sleek_auth_kind';           // realm that authenticated: 'tenant' | 'admin'
const K_SURFACE = 'sleek_auth_surface';     // side the user chose: 'companies' | 'admin' (unified login #8)
const K_LOGIN_USER = 'sleek_login_user';   // remembered username for prefill (survives logout)
// '1' when /api/me reports a platform_admin grant. Lets a granted admin who holds only a TENANT
// login use the console (one-credential model, owner 2026-09-14). Like K_IMPERSONATION it is NOT in
// clearAll() (must survive token refresh); cleared explicitly on login start and logout, and set
// after a tenant-realm sign-in on the admin surface.
const K_PLATFORM_ADMIN = 'sleek_platform_admin';
// '1' when /api/me reports the user is a partner admin (identity.partner_member owner/admin) — a
// per-country partner super admin, NOT a global platform admin. Set from /api/me at login; drives
// scoping the console to their companies (plan/partners.md Phase 1B). Cleared with K_PLATFORM_ADMIN.
const K_PARTNER_ADMIN = 'sleek_partner_admin';
// The partner this Regional Partner governs (JSON of /api/me user.partners[0]: partner_id, role,
// partner_name, country_id, country_name). Feeds the console's region lock + "Regional Partner ·
// <country>" label. Set/cleared alongside K_PARTNER_ADMIN.
const K_PARTNER = 'sleek_partner';
// Owned by shared/impersonate.js — cleared here (by name, to keep this foundational module out of
// impersonate.js's import cycle) so a fresh sign-in or sign-out is NEVER mid-impersonation. Without
// this, a leftover grant reroutes an admin to the tenant app on their next login and 401s them
// straight back out. NOT cleared in clearAll()/persist() — those run on every token refresh, which
// must not disturb a live impersonation session.
const K_IMPERSONATION = 'sleek_impersonation';
function clearImpersonationRecord(){
  try { localStorage.removeItem(K_IMPERSONATION); sessionStorage.removeItem(K_IMPERSONATION); } catch {}
}
function clearPlatformAdmin(){
  try {
    localStorage.removeItem(K_PLATFORM_ADMIN); sessionStorage.removeItem(K_PLATFORM_ADMIN);
    localStorage.removeItem(K_PARTNER_ADMIN); sessionStorage.removeItem(K_PARTNER_ADMIN);
    localStorage.removeItem(K_PARTNER); sessionStorage.removeItem(K_PARTNER);
  } catch {}
}

function remembered(){ return localStorage.getItem(K_REMEMBER) === '1'; }
function getItem(k){ return localStorage.getItem(k) || sessionStorage.getItem(k); }
function clearAll(){   // session teardown — deliberately keeps K_LOGIN_USER for "remember me"
  for(const s of [localStorage, sessionStorage])
    [K_TOKEN, K_REFRESH, K_USER, K_REMEMBER, K_REALM, K_CLIENT, K_KIND, K_SURFACE].forEach(k => s.removeItem(k));
}

export const currentUser = signal(getItem(K_USER) || '');
export function token(){ return getItem(K_TOKEN); }
export function isAuthed(){ return !!token(); }
export function authKind(){ return getItem(K_KIND) || ''; }   // 'tenant' | 'admin' — realm that authed
// Grant-aware: a sleek-admin realm token is an admin, AND so is a granted platform admin who signed
// in with a TENANT login (K_PLATFORM_ADMIN, set from /api/me at login). The server re-checks every
// /api/admin/* call (keycloak_auth.require_admin), so this only drives the UI/routing.
export function isAdmin(){ return authKind() === 'admin' || getItem(K_PLATFORM_ADMIN) === '1'; }
// A GLOBAL platform admin (the console's real audience). Set reliably from /api/me at login for BOTH
// realms, so it is trustworthy even for a sleek-admin-realm login (where isAdmin() is realm-based).
export function isPlatformAdmin(){ return getItem(K_PLATFORM_ADMIN) === '1'; }
// A per-country PARTNER admin (owner/admin) — scoped to their country's companies, not the console.
export function isPartnerAdmin(){ return getItem(K_PARTNER_ADMIN) === '1'; }
// The Regional Partner's governed partner (from /api/me), or null. Drives the region lock + label.
export function partnerInfo(){ try { return JSON.parse(getItem(K_PARTNER) || 'null'); } catch { return null; } }
export function partnerCountryId(){ const p = partnerInfo(); return (p && p.country_id) || null; }
export function partnerRegionName(){ const p = partnerInfo(); return (p && p.country_name) || ''; }
// Which side the user is using — decoupled from the realm (#8 unified login): a platform admin can
// sign in with their sleek-admin credential on the COMPANIES side (realm falls back to admin) and
// still belong on the tenant app. `isAdmin()` stays "holds an admin token"; `surface()` is "chose
// companies vs the console". Defaults from the realm for sessions that predate this key.
export function surface(){
  const s = getItem(K_SURFACE);
  if(s === 'admin' || s === 'companies') return s;
  return isAdmin() ? 'admin' : 'companies';   // unset / legacy value → derive from the realm
}
export function inCompaniesMode(){ return surface() === 'companies'; }
export function rememberedUsername(){ return localStorage.getItem(K_LOGIN_USER) || ''; }

function claimsOf(jwt){
  try { return JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); }
  catch { return {}; }
}

let refreshTimer = null;
function scheduleRefresh(){
  clearTimeout(refreshTimer);
  const t = token();
  if(!t) return;
  // 60s before expiry, minus per-tab jitter so shared-session tabs don't race the
  // one-time refresh token at the same instant
  const ms = (claimsOf(t).exp || 0) * 1000 - Date.now() - 60000 - Math.floor(Math.random() * 15000);
  if(ms > 0 && ms < 24 * 3600 * 1000) refreshTimer = setTimeout(() => { refresh(); }, ms);
}

// Inactivity sign-out lives in shared/idle.js (15 minutes with no activity in ANY aboo window, then a
// 60-second "Still there?" countdown), started by both shells. The earlier silent 3-minute, per-tab
// timer here signed out every window whenever ONE background window sat untouched for 3 minutes, so a
// person working in another aboo window was thrown to the login screen (Sami, 2026-10-05).

function persist(data, remember, ctx){
  clearAll();
  const s = remember ? localStorage : sessionStorage;
  s.setItem(K_TOKEN, data.access_token);
  if(data.refresh_token) s.setItem(K_REFRESH, data.refresh_token);
  s.setItem(K_REMEMBER, remember ? '1' : '0');
  s.setItem(K_REALM, ctx.realm);
  s.setItem(K_CLIENT, ctx.client);
  s.setItem(K_KIND, ctx.kind);
  s.setItem(K_SURFACE, ctx.surface || (ctx.kind === 'admin' ? 'admin' : 'companies'));
  const c = claimsOf(data.access_token);
  const name = c.name || c.preferred_username || '';
  s.setItem(K_USER, name);
  currentUser.value = name;
  scheduleRefresh();
}

async function grant(ctx, params){
  return fetch(tokenUrl(ctx.realm), {
    method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: ctx.client, scope: 'openid', ...params }),
  });
}

// Ask the server whether this freshly-authenticated user holds a platform_admin grant, and remember
// it so isAdmin() opens the console for a granted admin who signed in with a TENANT login. Raw fetch,
// not the api client — that client imports THIS module (a cycle). Best-effort: a failure reads as
// "not an admin", which the caller treats as a refused admin sign-in.
// Ask the server what kind of admin this freshly-authenticated user is, and remember it.
// Returns {ok, platform, partner}: `platform` = a global platform_admin grant (opens the console),
// `partner` = a per-country partner admin (scoped to their companies). `ok` is false when /api/me
// could not be read, so callers can keep the status-quo (realm-based) behaviour rather than lock out.
async function loadAdminFlags(remember){
  try {
    const res = await fetch('/api/me', { headers: { Authorization: 'Bearer ' + token() } });
    if(!res.ok) return { ok: false, platform: false, partner: false };
    const d = await res.json();
    const platform = !!(d && d.user && d.user.is_platform_admin);
    const partner = !!(d && d.user && d.user.is_partner_admin);
    const store = remember ? localStorage : sessionStorage;
    if(platform) store.setItem(K_PLATFORM_ADMIN, '1');
    if(partner){
      store.setItem(K_PARTNER_ADMIN, '1');
      const p = (d.user.partners || []).find(x => x.role === 'owner' || x.role === 'admin') || (d.user.partners || [])[0];
      if(p) store.setItem(K_PARTNER, JSON.stringify(p));   // region lock + label source
    }
    return { ok: true, platform, partner };
  } catch { return { ok: false, platform: false, partner: false }; }
}

// `surface` (optional) is the SIDE the user chose on the login screen — 'companies' or 'admin' —
// not the realm (owner 2026-09-10, #8 unified login). 'admin' authenticates against the admin realm
// only. 'companies' (and the legacy null) try the tenant realm first, then FALL BACK to the admin
// realm — so a platform admin can sign in on the companies side with their sleek-admin credential
// and still land on the tenant app, while a pure tenant user is unaffected (the tenant realm still
// wins first). The realm that succeeds is remembered for token refresh; the surface is remembered
// separately for routing (persist()).
export async function login(username, password, remember = true, surface = null){
  // On the admin surface, try the admin realm FIRST (real platform staff) then FALL BACK to the
  // tenant realm — a platform admin added through the Administrators screen holds only a tenant login
  // (the one realm Sleek can provision). The companies surface keeps the tenant-first order.
  const realms = surface === 'admin'
    ? REALMS.filter(r => r.kind === 'admin').concat(REALMS.filter(r => r.kind !== 'admin'))
    : REALMS;
  clearPlatformAdmin();   // fresh sign-in — never inherit a previous session's grant flag
  let lastErr;
  for(const ctx of realms){
    try {
      const res = await grant(ctx, { grant_type: 'password', username, password });
      if(res.ok){
        persist(await res.json(), remember, { ...ctx, surface: surface || null });
        // Admin-surface sign-in: resolve what kind of admin this is from the server.
        //  * platform admin   → the full console (K_PLATFORM_ADMIN; isAdmin() opens it).
        //  * Regional Partner → the console too, but REGION-SCOPED (Phase 1C): the shell shows a
        //    scoped nav locked to their country and the admin API scopes every list to it. They stay
        //    on the 'admin' surface (set by persist() above) — no longer bounced to the tenant app.
        //  * neither, on a TENANT login → refuse (a non-admin must not land in a console that 401s
        //    every call). An admin-REALM login with neither keeps the console (status quo; the realm
        //    is the historical gate) — and a /api/me read failure (flags.ok=false) never locks out.
        if(surface === 'admin'){
          const flags = await loadAdminFlags(remember);
          if(ctx.kind !== 'admin' && !flags.platform && !flags.partner){
            clearAll(); clearPlatformAdmin(); currentUser.value = '';
            lastErr = new Error('This account does not have admin access.');
            continue;
          }
        }
        clearImpersonationRecord();   // a fresh sign-in is never mid-impersonation
        if(remember) localStorage.setItem(K_LOGIN_USER, username); else localStorage.removeItem(K_LOGIN_USER);
        return;
      }
      const e = await res.json().catch(() => ({}));
      lastErr = new Error(e.error_description || e.error || `Sign-in failed (${res.status})`);
    } catch(err){ lastErr = err; }
  }
  throw lastErr || new Error('Sign-in failed');
}

export async function refresh(){
  const rt = getItem(K_REFRESH);
  const realm = getItem(K_REALM), client = getItem(K_CLIENT), kind = getItem(K_KIND);
  const surface = getItem(K_SURFACE);   // preserve the chosen side across refresh (persist clears it)
  if(!rt || !realm || !client) return false;
  const keep = remembered();
  const tokenBefore = token();
  try {
    const res = await grant({ realm, client }, { grant_type: 'refresh_token', refresh_token: rt });
    if(!res.ok){
      // Keycloak ROTATES refresh tokens: with "remember me" the session is shared across
      // tabs, and whichever tab refreshes first invalidates this one's copy. Losing the
      // race must adopt the winner's tokens — never clear the shared session.
      if(token() && token() !== tokenBefore){ scheduleRefresh(); return true; }
      if(getItem(K_REFRESH) && getItem(K_REFRESH) !== rt) return refresh();
      return false;
    }
    persist(await res.json(), keep, { realm, client, kind, surface });
    return true;
  } catch { return false; }
}

// mirror another tab's refresh/sign-out into this tab (fires on localStorage changes)
if(typeof window !== 'undefined'){
  window.addEventListener('storage', e => {
    if(e.key === K_TOKEN){
      currentUser.value = getItem(K_USER) || '';
      if(e.newValue) scheduleRefresh(); else clearTimeout(refreshTimer);
    }
  });
}

export function logout(){
  clearTimeout(refreshTimer);
  clearAll();
  clearPlatformAdmin();   // grant flag is not in clearAll() (it survives refresh) — drop it on sign-out
  clearImpersonationRecord();   // sign-out ends any support-access grant too — never leave it to brick the next login
  currentUser.value = '';   // local sign-out only — never a Keycloak logout screen
}

if(token()) scheduleRefresh();   // keep an existing session fresh on load
